import type { OTomTat } from '@qlk/shared';

import { Skeleton } from '../../../../ui/index.js';
import { phanTram } from '../dinhDang.js';
import { dinhDangChiSo } from './dinhDangChiSo.js';

/**
 * Thanh bốn ô tóm tắt đầu mỗi tab — **không bấm được**.
 *
 * Khác `HangThe` (`kieu="o"`) của tab Tổng quan ở đúng một chỗ: ở đó ô là nút,
 * bấm vào đổi cả trang; ở đây ô chỉ là con số. Nên dùng `<div>` chứ không
 * `<button>`: một thẻ bấm được mà bấm không ra gì là lời hứa suông với cả
 * chuột lẫn bàn phím.
 *
 * Mọi thước còn lại giữ nguyên để bốn tab trông là một trang: căn trái, padding
 * 18px ngang, vạch 1px `--color-vien-trong` giữa các ô, 2 cột ở 390px.
 */

export interface ThanhBonOProps {
  o: OTomTat[];
  dangTai: boolean;
  currency?: string;
}

export function ThanhBonO({ o, dangTai, currency = 'USD' }: ThanhBonOProps) {
  if (dangTai) return <Skeleton className="h-[108px] w-full rounded-the" />;
  if (o.length === 0) return null;

  return (
    <div
      className="grid gap-px overflow-hidden rounded-the border border-vien bg-vien-trong grid-cols-2 @min-[705px]:grid-cols-4"
      data-khoi="bon-o"
    >
      {o.map((x) => (
        <div
          key={x.khoa}
          data-o={x.khoa}
          /* Giá trị **thô** cho bài e2e: số trên màn hình in gọn (`$5,6K`), nên
             không so chữ được với trang Doanh thu in đầy đủ. `null` thành chuỗi
             rỗng — "chưa có số liệu" khác hẳn số 0. */
          data-gia-tri={x.giaTri === null ? '' : String(x.giaTri)}
          className="@container flex flex-col items-start gap-1 bg-be-mat px-[18px] py-4"
        >
          <span className="flex flex-wrap items-center gap-2 text-bang text-chu-phu">
            {x.nhan}
            {x.chip !== null && (
              <span className="rounded-chip bg-warn-nen px-2 py-0.5 text-phu font-semibold text-warn-chu">
                {x.chip}
              </span>
            )}
          </span>

          {/* Cỡ số tự thu theo bề ngang **của chính ô**, như hàng thẻ — hai cột
              trên điện thoại cho mỗi ô ~175px, cỡ 26px thì số gãy đôi. */}
          <span className="so-lieu whitespace-nowrap text-tieu-de-hop font-bold text-chu @min-[185px]:text-so-lon @min-[240px]:text-so-rat-lon">
            {dinhDangChiSo(x.giaTri, x.dinhDang, currency)}
          </span>

          <DongPhu phu={x.phu} delta={x.deltaPercent} />
        </div>
      ))}
    </div>
  );
}

/**
 * Dòng thứ ba: ghi chú riêng của ô, **hoặc** mức đổi so kỳ trước.
 *
 * Ghi chú đứng trước: ô "Video đã đăng trong kỳ" nói "trên 7 kênh · 3 Shorts",
 * và một mũi tên phần trăm cạnh đó không nói thêm gì. Ô nào có cả hai thì ghi
 * chú thắng — nó cụ thể hơn.
 */
function DongPhu({ phu, delta }: { phu: string | null; delta: number | null }) {
  if (phu !== null) return <span className="text-bang text-chu-mo">{phu}</span>;
  if (delta === null)
    return <span className="text-bang text-chu-mo">Chưa so được với kỳ trước</span>;

  const tang = delta >= 0;
  return (
    <span
      data-huong={tang ? 'tang' : 'giam'}
      className={`so-lieu text-bang font-semibold ${tang ? 'text-tang' : 'text-giam'}`}
    >
      {tang ? '▲' : '▼'} {phanTram(Math.abs(delta))} so kỳ trước
    </span>
  );
}
